/**
 * Which roastery's portal this is, read off the address (US-10.5).
 *
 * <p>Two shapes, one meaning. In production a roastery gets a subdomain —
 * `caferynek.portal.example.com` — because that is what a white-label address looks like to the
 * customer receiving the link. Locally (and anywhere wildcard DNS or a wildcard certificate is not
 * worth setting up) the same roastery is `portal.localhost/caferynek`, and the application runs from
 * that path as its base.
 *
 * <p><strong>The slug decides how the portal looks, never what it may show.</strong> It is a hint for
 * the screens that exist before a token does: the name and colour on the way to Keycloak, and on
 * "your access has ended". Once the token is there, the roastery comes from it — a buyer who follows
 * somebody else's link and signs in ends up in their own roastery, not in the one that owns the link.
 *
 * <p>This module imports nothing on purpose. The path form has to know the application's own route
 * names, and the obvious way to get them — reading the generated route tree — makes a cycle, because
 * every route file leads back here through the branding. So the caller passes them in, and
 * {@link initTenantAddress} runs once in `main.tsx`, where the route tree already lives.
 */
export type TenantAddress = {
  /** The roastery named by the address, if the address names one. */
  slug?: string;
  /** What every route hangs off: "/" or "/{slug}". */
  basepath: string;
};

/** Slugs are what Keycloak organisations and URLs allow: lower case, digits, hyphens. */
const SLUG = /^[a-z0-9][a-z0-9-]{0,49}$/;

const ROOT: TenantAddress = { basepath: "/" };

let resolved: TenantAddress = ROOT;

/**
 * @param reserved the application's own first path segments, so a screen called `orders` can never be
 *                 mistaken for a roastery. The reverse still holds — a roastery whose slug is
 *                 literally `orders` would be unreachable in the path form — but that is a caveat of
 *                 the local form only: production addresses a roastery by subdomain, which shares
 *                 nothing with the route table.
 */
export function resolveTenantAddress(
  reserved: ReadonlySet<string>,
  location: Location = window.location,
): TenantAddress {
  const labels = location.hostname.split(".");
  const subdomain = labels[0];
  if (labels.length >= 3 && labels[1] === "portal" && subdomain && SLUG.test(subdomain)) {
    return { slug: subdomain, basepath: "/" };
  }
  const first = location.pathname.split("/").filter(Boolean)[0];
  if (first && SLUG.test(first) && !reserved.has(first)) {
    return { slug: first, basepath: `/${first}` };
  }
  return ROOT;
}

/** Resolved once, at start-up: the router's basepath is fixed when the router is built. */
export function initTenantAddress(reserved: ReadonlySet<string>): TenantAddress {
  resolved = resolveTenantAddress(reserved);
  return resolved;
}

/** The address this portal was opened at. Answers the root portal until start-up has resolved it. */
export function tenantAddress(): TenantAddress {
  return resolved;
}

/** Where Keycloak sends the browser back — the roastery's own address, not the bare origin. */
export function addressOrigin(address: TenantAddress, location: Location = window.location): string {
  return address.basepath === "/" ? location.origin : location.origin + address.basepath;
}
